import React, { useState, useEffect, ChangeEvent, FC } from "react";
import cn from "classnames";
import { NumericFormat } from "react-number-format";
import { useTranslation } from "react-i18next";
import { OnChangeRange } from "src/@types/form";
import { checkIsValidRangePrice } from "./utils/checkIsValidRangePrice";
import { filteringTrns } from "./trns";
import { IPriceRangeSlider } from "./types";
import { RangeSlider } from "../RangeSlider";

const DEFAULT_WRAPPER_INPUT_CLASSNAME =
  "flex items-center justify-between gap-5";

const DEFAULT_LABEL_INPUT_CLASSNAME =
  "flex gap-1 items-center w-full sm:max-w-48 h-8 py-2 px-3 text-lg leading-normal border border-black-base/20 rounded-20 outline-none placeholder-purple-base/50 placeholder:text-lg";

const DEFAULT_LABEL_INPUT_ERROR_CLASSNAME = "text-red-light border-red-light";

const DEFAULT_INPUT_CLASSNAME = "w-full bg-inherit";
const DEFAULT_INPUT_ERROR_CLASSNAME = "placeholder-red-light/50";

const THOUSAND_SEPARATOR = " ";

export const PriceRangeSlider: FC<IPriceRangeSlider> = ({
  initialMin,
  initialMax,
  min,
  max,
  onChange,
  currency,
}) => {
  const { t } = useTranslation();

  const intMin = parseInt(min);
  const intMax = parseInt(max);

  const intMinPrice = parseInt(initialMin);
  const intMaxPrice = parseInt(initialMax);

  const [isValidMaxPrice, setIsValidMaxPrice] = useState(
    checkIsValidRangePrice(initialMin, intMin, intMaxPrice) // сhecking whether the initial values ​​we receive from the URL are valid
  );
  const [isValidMinPrice, setIsValidMinPrice] = useState(
    checkIsValidRangePrice(initialMax, intMinPrice, intMax) // сhecking whether the initial values ​​we receive from the URL are valid
  );

  const currentInitialMin = isValidMinPrice ? intMinPrice : intMin;
  const currentInitialMax = isValidMaxPrice ? intMaxPrice : intMax;

  const onChangeMinPrice = (event: ChangeEvent<HTMLInputElement>) => {
    const newMinPrice = event.target.value.replaceAll(" ", "");

    const isValidNewMinPrice = checkIsValidRangePrice(
      newMinPrice,
      intMin,
      intMaxPrice
    );

    setIsValidMinPrice(isValidNewMinPrice);

    const minPrice = isValidNewMinPrice ? parseInt(newMinPrice) : intMin;

    onChange(minPrice, currentInitialMax);
  };

  const onChangeMaxPrice = (event: ChangeEvent<HTMLInputElement>) => {
    const newMaxPrice = event.target.value.replaceAll(" ", "");

    const isValidNewMaxPrice = checkIsValidRangePrice(
      newMaxPrice,
      intMinPrice,
      intMax
    );

    setIsValidMaxPrice(isValidNewMaxPrice);

    const maxPrice = isValidNewMaxPrice ? parseInt(newMaxPrice) : intMax;

    onChange(currentInitialMin, maxPrice);
  };

  const onChangeRange: OnChangeRange = (min, max) => {
    // The values from the range are always correct,
    // so we set setIsValidMaxPrice and setIsValidMinPrice to true
    setIsValidMaxPrice(true);
    setIsValidMinPrice(true);
    onChange(min, max);
  };

  useEffect(() => {
    // if, upon the first transition to the page,
    // the value from the searchParams is invalid (manually entered beyond the allowable range),
    // then we set the default min and max values

    if (!isValidMinPrice || !isValidMaxPrice) {
      setIsValidMaxPrice(true);
      setIsValidMinPrice(true);
      onChange(intMin, intMax);
    }
  }, []);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col sm:flex-row gl:flex-col gl:justify-between gap-2.5">
        <div className={DEFAULT_WRAPPER_INPUT_CLASSNAME}>
          <span>{t(filteringTrns.fromLabel)}</span>

          <label
            className={cn(DEFAULT_LABEL_INPUT_CLASSNAME, {
              [DEFAULT_LABEL_INPUT_ERROR_CLASSNAME]: !isValidMinPrice,
            })}
          >
            <span>{currency}</span>

            <NumericFormat
              className={cn(DEFAULT_INPUT_CLASSNAME, {
                [DEFAULT_INPUT_ERROR_CLASSNAME]: !isValidMinPrice,
              })}
              thousandSeparator={THOUSAND_SEPARATOR}
              placeholder={t(filteringTrns.minPlaceholder)}
              value={isValidMinPrice ? initialMin : undefined} // if the value is invalid we stop controlling the input
              onChange={onChangeMinPrice}
            />
          </label>
        </div>

        <div className={DEFAULT_WRAPPER_INPUT_CLASSNAME}>
          <span>{t(filteringTrns.toLabel)}</span>
          <label
            className={cn(DEFAULT_LABEL_INPUT_CLASSNAME, {
              [DEFAULT_LABEL_INPUT_ERROR_CLASSNAME]: !isValidMaxPrice,
            })}
          >
            <span>{currency}</span>

            <NumericFormat
              className={cn(DEFAULT_INPUT_CLASSNAME, {
                [DEFAULT_INPUT_ERROR_CLASSNAME]: !isValidMaxPrice,
              })}
              thousandSeparator={THOUSAND_SEPARATOR}
              placeholder={t(filteringTrns.maxPlaceholder)}
              value={isValidMaxPrice ? initialMax : undefined} // if the value is invalid we stop controlling the input
              onChange={onChangeMaxPrice}
            />
          </label>
        </div>
      </div>
      <RangeSlider
        initialMin={currentInitialMin}
        initialMax={currentInitialMax}
        min={intMin}
        max={intMax}
        onChangeRange={onChangeRange}
      />
    </div>
  );
};
